import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Machine } from '../services/BackendDataService';
import { 
  getLineKPIConfig, 
  saveLineKPIConfig,
  type MachineKPIConfig 
} from '../services/LineConfigService';
import { applyFlowLayout } from '../services/FlowLayoutService';
import { invalidateLineStations } from './line/useLineData';
import { useUnifiedAuth } from '../auth/useUnifiedAuth';
import { useSAPDataStore } from '../store/SAPDataStore';
import { resolveAll } from '../services/KpiTargetService';
import { KpiTargetFields } from './targets/KpiTargetFields';
import { parseInputs, toInputs, type TargetInputs } from './targets/kpiTargetForm';
import { getMachineTypeCategory } from '../utils/sapMetadataTranslations';
import { cx } from '../styles';
import { Spinner, useToast } from '@digitalisation/enlixy-comp';
import { useThemeClasses } from '../providers/ThemeProvider';

interface LineConfigPanelProps {
  lineId: string;
  lineName: string;
  machines: Machine[];
  isOpen: boolean;
  onClose: () => void;
}

interface MachineConfigRow {
  machineId: string;
  machineName: string;
  machineType: string;
  unitName: string;
  unitsPerContainer: string;
  flowOrder: number;
}

/** A machine's id as the line config and the statements use it (its sn). */
const machineIdOf = (m: Machine) => m.maschinennummer || m.maschineBK;

const LineConfigPanel: React.FC<LineConfigPanelProps> = ({
  lineId,
  lineName,
  machines,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { token } = useUnifiedAuth();
  const themeClasses = useThemeClasses();
  
  const [machineConfigs, setMachineConfigs] = useState<MachineConfigRow[]>([]);
  const [outputUnitDescription, setOutputUnitDescription] = useState('');
  // the machine the line output is counted at; undefined = automatic
  const [outputMachineId, setOutputMachineId] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Line targets: the KPI targets of the line (kpi_targets, its machines use
  // them unless they have their own; output per shift is the line output's).
  const kpiTargetIndex = useSAPDataStore((s) => s.kpiTargetIndex);
  const kpiTargetsLoaded = useSAPDataStore((s) => s.kpiTargetsLoaded);
  const fetchKpiTargets = useSAPDataStore((s) => s.fetchKpiTargets);
  const saveKpiTarget = useSAPDataStore((s) => s.saveKpiTarget);
  const lineRow = kpiTargetIndex.get(`line:${lineId}`);
  // the line's targets as loaded: saving sends them only when they changed, so
  // targets that did not load are never cleared by a save
  const loadedInputs = useMemo(() => toInputs(lineRow?.targets), [lineRow]);
  const [targetInputs, setTargetInputs] = useState<TargetInputs>(() => toInputs(lineRow?.targets));
  const [invalidTarget, setInvalidTarget] = useState<keyof TargetInputs | null>(null);
  // the machine-type targets apply where the line has none (one type per line usually)
  const lineTypes = useMemo(
    () => [...new Set(machines.map((m) => m.bautyp || m.machineType).filter(Boolean))] as string[],
    [machines],
  );
  // (not output: the line's is its line output, the type's a machine count)
  const fromType = useMemo(() => {
    if (lineTypes.length !== 1) return undefined;
    const inherited = resolveAll(kpiTargetIndex, { machineType: lineTypes[0] });
    delete inherited.output;
    return inherited;
  }, [kpiTargetIndex, lineTypes]);
  
  // Sort machines by flow order and filter only machines with data
  const sortedMachines = useMemo(() => {
    const withData = machines.filter(m => m.datenVerfuegbar);
    return applyFlowLayout(lineId, withData);
  }, [lineId, machines]);

  // Without a choice the PC.line_output statements count the line's last flow
  // group (highest order_line), any machine of the line, with data or not.
  const autoOutput = useMemo(() => {
    const groups = machines.map((m) => m.flowGroup).filter((g): g is number => typeof g === 'number');
    if (groups.length === 0) return [];
    const last = Math.max(...groups);
    return machines.filter((m) => m.flowGroup === last);
  }, [machines]);
  
  // Initialize config from backend
  useEffect(() => {
    if (!isOpen || !token) return;
    
    const loadConfig = async () => {
      const savedConfig = await getLineKPIConfig(lineId, token);
      
      const configRows: MachineConfigRow[] = sortedMachines.map((machine) => {
        const machineId = machineIdOf(machine);
        const savedMachineConfig = savedConfig?.machines.find(m => m.machineId === machineId);
        
        // Build display name
        let rawType = machine.bautyp || machine.machineType || '';
        const machineNum = machine.maschinennummer || machine.maschineBK || '';
        if (rawType && machineNum && rawType.startsWith(machineNum)) {
          rawType = rawType.replace(new RegExp(`^${machineNum}\\s*[-–]?\\s*`), '').trim();
        }
        const modelName = rawType.replace(/^Modell\s+/i, '');
        const typeCategory = getMachineTypeCategory(machine.bautyp || machine.machineType || '');
        
        return {
          machineId,
          machineName: modelName || machineNum,
          machineType: typeCategory !== 'unknown' ? typeCategory : rawType,
          unitName: savedMachineConfig?.unitConversion?.unitName || '',
          unitsPerContainer: savedMachineConfig?.unitConversion?.unitsPerContainer
            ? String(savedMachineConfig.unitConversion.unitsPerContainer)
            : '',
          flowOrder: machine.flowOrder ?? 0,
        };
      });
      
      setMachineConfigs(configRows);
      setOutputMachineId(savedConfig?.outputMachineId || undefined);
      setOutputUnitDescription(savedConfig?.outputUnitDescription || '');
    };
    
    loadConfig();
  }, [isOpen, lineId, sortedMachines, token]);

  useEffect(() => {
    if (isOpen) setTargetInputs(toInputs(lineRow?.targets));
  }, [isOpen, lineRow]);

  // the targets are loaded once per session: read them again on open, so the
  // fields show what others (or a migration) saved since
  useEffect(() => {
    if (isOpen && token) void fetchKpiTargets(token);
  }, [isOpen, token, fetchKpiTargets]);
  
  const handleUnitNameChange = (machineId: string, value: string) => {
    setMachineConfigs(prev => prev.map(c => 
      c.machineId === machineId ? { ...c, unitName: value } : c
    ));
  };
  
  const handleUnitsPerContainerChange = (machineId: string, value: string) => {
    setMachineConfigs(prev => prev.map(c => 
      c.machineId === machineId ? { ...c, unitsPerContainer: value } : c
    ));
  };
  
  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    
    try {
      // Build machine configs. "Contains" is the factor the line output is
      // multiplied by at the output machine, so it is kept without a unit name.
      const machinesConfig: MachineKPIConfig[] = machineConfigs.map(c => {
        const config: MachineKPIConfig = {
          machineId: c.machineId,
        };
        
        const unitsNum = parseFloat(c.unitsPerContainer);
        if (!isNaN(unitsNum) && unitsNum > 0) {
          config.unitConversion = {
            unitName: c.unitName,
            unitsPerContainer: unitsNum,
          };
        }
        
        return config;
      });
      
      const parsed = parseInputs(targetInputs);
      if ('invalid' in parsed) {
        setInvalidTarget(parsed.invalid);
        throw new Error(t('kpiTargets.invalid'));
      }
      setInvalidTarget(null);

      // the line's targets, output per shift too, are KPI targets (below)
      const configToSave = {
        machines: machinesConfig,
        outputUnitDescription: outputUnitDescription || undefined,
        outputMachineId,
      };
      
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const success = await saveLineKPIConfig(lineId, configToSave, token);
      
      if (!success) {
        throw new Error('Failed to save');
      }
      // the line charts mark the output machine: load the new choice next time
      invalidateLineStations(lineId);
      const targetsChanged = (Object.keys(targetInputs) as (keyof TargetInputs)[]).some(
        (kpi) => targetInputs[kpi] !== loadedInputs[kpi],
      );
      if (targetsChanged) {
        // without loaded targets a save would replace a row nobody saw
        if (!kpiTargetsLoaded) throw new Error(t('kpiTargets.notLoaded'));
        await saveKpiTarget('line', lineId, parsed.targets, token);
      }
      
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to save line config:', error);
      toast.error(error, t('saveError') || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  // Check if any unit conversions are configured
  const hasUnitConversions = machineConfigs.some(c => c.unitName && c.unitsPerContainer);

  // The machines the line output is counted at: the chosen one, else the last
  // flow group's (several when they run in parallel). Machines without data
  // have no row, so they are named in the "automatic" option.
  const rowIds = new Set(machineConfigs.map((c) => c.machineId));
  const outputIds = new Set(outputMachineId ? [outputMachineId] : autoOutput.map(machineIdOf));
  const labelOf = (id: string) =>
    rowIds.has(id)
      ? machineConfigs.find((c) => c.machineId === id)?.machineName || id
      : `${id} (${t('outputMachine.noData')})`;
  const autoText =
    autoOutput.length === 0
      ? t('outputMachine.autoNone')
      : t('outputMachine.autoIs', { machines: autoOutput.map((m) => labelOf(machineIdOf(m))).join(', ') });
  const radioName = `line-output-${lineId}`;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className={cx(
        'w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-xl shadow-2xl',
        themeClasses.bgSurface
      )}>
        {/* Header */}
        <div className={cx(
          'flex items-center justify-between px-6 py-4 border-b',
          themeClasses.borderPrimary
        )}>
          <div>
            <h2 className={cx('text-xl font-bold', themeClasses.textPrimary)}>
              {t('lineKPIConfiguration') || 'Line KPI Configuration'}
            </h2>
            <p className={cx('text-sm mt-1', themeClasses.textSecondary)}>
              {lineName}
            </p>
          </div>
          <button
            onClick={onClose}
            className={cx(
              'p-2 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-colors',
              themeClasses.textSecondary
            )}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6 space-y-6">
          {/* Unit Conversions */}
          <div>
            <h3 className={cx('text-lg font-semibold mb-3', themeClasses.textPrimary)}>
              {t('packagingStages') || 'Packaging Stages'} <span className={themeClasses.textMuted}>({t('optional') || 'optional'})</span>
            </h3>
            
            <div className={cx('border rounded-lg overflow-hidden', themeClasses.borderPrimary)}>
              <table className="w-full">
                <thead className={cx('bg-gray-50 dark:bg-gray-800')}>
                  <tr>
                    <th className={cx('px-4 py-3 text-left text-sm font-medium', themeClasses.textSecondary)}>
                      {t('machine') || 'Machine'}
                    </th>
                    <th className={cx('px-4 py-3 text-left text-sm font-medium', themeClasses.textSecondary)}>
                      {t('produces') || 'Produces'}
                    </th>
                    <th className={cx('px-4 py-3 text-left text-sm font-medium', themeClasses.textSecondary)}>
                      {t('containsItems') || 'Contains (items)'}
                    </th>
                  </tr>
                </thead>
                <tbody className={cx('divide-y', themeClasses.borderPrimary)}>
                  {machineConfigs.map((config, index) => {
                    // Dynamic placeholders based on position
                    const placeholders = ['packets', 'cartridges', 'boxes', 'pallets'];
                    const placeholder = placeholders[index] || 'units';
                    const isFirst = index === 0;
                    
                    return (
                      <tr 
                        key={config.machineId}
                        className={cx(
                          outputIds.has(config.machineId) ? 'bg-green-50 dark:bg-green-900/10' : '',
                          'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                        )}
                      >
                        <td className={cx('px-4 py-3', themeClasses.textPrimary)}>
                          {/* the radio chooses the machine the line output is counted at */}
                          <label className="flex cursor-pointer items-center gap-2">
                            <input
                              type="radio"
                              name={radioName}
                              checked={outputMachineId === config.machineId}
                              onChange={() => setOutputMachineId(config.machineId)}
                              aria-label={t('outputMachine.choose', { machine: config.machineName })}
                              className="h-4 w-4 shrink-0 cursor-pointer accent-brand"
                            />
                            <span className="text-sm font-mono text-gray-400">{index + 1}.</span>
                            <span className="font-medium">{config.machineName}</span>
                            {outputIds.has(config.machineId) && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-300">
                                {t('output') || 'output'}
                              </span>
                            )}
                          </label>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={config.unitName}
                            onChange={(e) => handleUnitNameChange(config.machineId, e.target.value)}
                            placeholder={placeholder}
                            className={cx(
                              'w-full px-3 py-1.5 text-sm rounded border',
                              themeClasses.bgSurface,
                              themeClasses.borderPrimary,
                              themeClasses.textPrimary,
                              'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                            )}
                          />
                        </td>
                        <td className="px-4 py-3">
                          {isFirst ? (
                            <span className={cx('text-sm italic', themeClasses.textMuted)}>
                              {t('baseUnit') || '— base unit —'}
                            </span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                step="1"
                                value={config.unitsPerContainer}
                                onChange={(e) => handleUnitsPerContainerChange(config.machineId, e.target.value)}
                                placeholder="10"
                                className={cx(
                                  'w-20 px-3 py-1.5 text-sm rounded border',
                                  themeClasses.bgSurface,
                                  themeClasses.borderPrimary,
                                  themeClasses.textPrimary,
                                  'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                                )}
                              />
                              {index > 0 && machineConfigs[index - 1]?.unitName && (
                                <span className={cx('text-sm', themeClasses.textMuted)}>
                                  {machineConfigs[index - 1].unitName}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* no choice: the statements' own rule (the last flow group) */}
            <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="radio"
                name={radioName}
                checked={outputMachineId === undefined}
                onChange={() => setOutputMachineId(undefined)}
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-brand"
              />
              <span>
                <span className={cx('font-medium', themeClasses.textPrimary)}>{t('outputMachine.auto')}</span>
                <span className={cx('block', themeClasses.textMuted)}>{autoText}</span>
              </span>
            </label>
            {outputMachineId && !rowIds.has(outputMachineId) && (
              <p className={cx('text-sm mt-2', themeClasses.textMuted)}>
                {t('outputMachine.chosenNoData', { machine: outputMachineId })}
              </p>
            )}
            <p className={cx('text-sm mt-2', themeClasses.textMuted)}>
              {t('outputMachine.help')} {t('packagingHelp') || 'Leave empty if all machines count the same unit.'}
            </p>
          </div>
          
          {/* Output Unit Description */}
          {hasUnitConversions && (
            <div>
              <label className={cx('block text-sm font-medium mb-2', themeClasses.textPrimary)}>
                {t('outputUnitDescription') || 'Final Output Description'}
              </label>
              <input
                type="text"
                value={outputUnitDescription}
                onChange={(e) => setOutputUnitDescription(e.target.value)}
                placeholder={t('egBoxesOf20Cartridges') || 'e.g., boxes of 20 cartridges (200 packets)'}
                className={cx(
                  'w-full px-4 py-2 rounded-lg border',
                  themeClasses.bgSurface,
                  themeClasses.borderPrimary,
                  themeClasses.textPrimary,
                  'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                )}
              />
            </div>
          )}
          
          {/* Line Targets (Optional) */}
          <div>
            <h3 className={cx('text-lg font-semibold mb-3', themeClasses.textPrimary)}>
              {t('lineTargets') || 'Line Targets'} <span className={themeClasses.textMuted}>({t('optional') || 'optional'})</span>
            </h3>
            <p className={cx('text-sm mb-3', themeClasses.textMuted)}>{t('kpiTargets.lineHint')}</p>
            <KpiTargetFields
              id={`line-${lineId}`}
              inputs={targetInputs}
              onChange={(kpi, value) => setTargetInputs((v) => ({ ...v, [kpi]: value }))}
              inherited={fromType}
              invalid={invalidTarget}
              hints={{ output: t('outputTarget.hint') }}
            />
          </div>
        </div>
        
        {/* Footer */}
        <div className={cx(
          'flex items-center justify-end gap-3 px-6 py-4 border-t',
          themeClasses.borderPrimary
        )}>
          {saveSuccess && (
            <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {t('saved') || 'Saved!'}
            </span>
          )}
          <button
            onClick={onClose}
            className={cx(
              'px-4 py-2 rounded-lg font-medium transition-colors',
              'text-gray-700 dark:text-gray-300',
              'hover:bg-gray-100 dark:hover:bg-gray-700'
            )}
          >
            {t('cancel') || 'Cancel'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={cx(
              'px-6 py-2 rounded-lg font-medium transition-colors',
              'bg-blue-600 text-white hover:bg-blue-700',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'flex items-center gap-2'
            )}
          >
            {saving && (
              <Spinner size={16} />
            )}
            {t('save') || 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LineConfigPanel;
